import { unwrap } from '../messageSource'
import type { ClearScope } from '../messageSource'
import type { DisplayMessage } from '../../types/display'
import { isSettled, MOMENT_LIFETIME_MS, type Row } from './contract'

/** What the screen should do with a row. */
export type Decision =
  /** Older than what is on screen, or the same row again. */
  | { kind: 'ignore' }
  /** Take the screen away. */
  | { kind: 'clear'; scope: ClearScope }
  /** Valid and newer, but a type this display has no screen for. */
  | { kind: 'unknown_type' }
  /** Show it. A moment also says when it ends and what is left after. */
  | { kind: 'show'; message: DisplayMessage; ageMs: number; endsInMs?: number; endScope?: ClearScope }

/** A short moment ends the sale, so the paid cart under it goes too. A QR
 *  that runs out leaves the customer looking at their order again. */
function scopeAfter(lifetime: number): ClearScope {
  return lifetime < 120_000 ? 'all' : 'moment'
}

/** Decides, row by row, what the screen shows. Pure: no timers, no network.
 *
 *  Rows apply in id order; the API serialises them per display. A settled or
 *  cleared row fences its id, so an older copy of the same row can never bring
 *  its screen back, and a reconnect or a status change never shows a payment
 *  moment twice. Both clocks are the server's, so the till's and the display's
 *  own clocks never decide how old a moment is. */
export function createDisplayGate() {
  let highestId = 0
  let highestFenced = false

  return {
    get highestId() {
      return highestId
    },

    offer(row: Row, serverNowMs: number): Decision {
      const fenced = row.payload.invalidated || isSettled(row.status)
      if (row.id < highestId || (row.id === highestId && (!fenced || highestFenced))) return { kind: 'ignore' }
      highestId = row.id
      highestFenced = fenced

      if (fenced || row.payload.message === null) return { kind: 'clear', scope: 'all' }

      const message = unwrap(row.payload.message)
      if (!message) return { kind: 'unknown_type' }

      const ageMs = serverNowMs - row.payload.serverUpdatedAtMs
      const lifetime = MOMENT_LIFETIME_MS[message.type]
      if (lifetime === undefined) return { kind: 'show', message, ageMs }
      if (ageMs >= lifetime) return { kind: 'clear', scope: scopeAfter(lifetime) }
      return { kind: 'show', message, ageMs, endsInMs: lifetime - ageMs, endScope: scopeAfter(lifetime) }
    },
  }
}
